import { isBridgeError, TeamsSendAction } from '@instant-messengers/vk-teams-bridge';
import { coreLoggerDispatchError } from '@mail-core/logger';
import axios from 'lib/axios';
import { CORSAPI_HOST, DOMAIN_HOST } from 'reactApp/appHelpers/configHelpers';
import type { BridgeType } from 'reactApp/services/WorkspaceSuperApp.class';

const AUTH_API_URL = `https://${CORSAPI_HOST}/auth/api/v1/silent_auth/sso`;
const isMiniMail = DOMAIN_HOST === 'mini-mail.ru';

const FETCH_TIMEOUT = 5000;

type SsoAuthResponseData = {
    status: number;
    body: { access_token: string };
};

export class TokenService {
    private authToken: string | null = null;

    constructor(private bridge: BridgeType) {}

    async init() {
        return await this.fetchAccessToken();
    }

    getAuthToken() {
        return this.authToken;
    }

    private async getSilentToken() {
        const timeoutPromise = new Promise((_resolve, reject) =>
            setTimeout(() => reject(new Error('Get silent token from superapp request timeout')), FETCH_TIMEOUT)
        );

        try {
            return await Promise.race([this.bridge.send(TeamsSendAction.GetAuth, null), timeoutPromise]);
        } catch (error) {
            if (error instanceof Error && error.message === 'GetAuth request timeout') {
                console.error(`GetAuth timed out after ${FETCH_TIMEOUT} ms`);
            } else if (isBridgeError(error)) {
                console.error('Get silent token bridge error', error);
            } else {
                console.error('Unexpected error in getSilentToken', error);
            }

            throw error;
        }
    }

    private async fetchAuthToken(silentToken: string) {
        try {
            const response = await axios.get(AUTH_API_URL, {
                params: {
                    silent_token: silentToken,
                    resource: isMiniMail ? 'https://cloud.mini-mail.ru' : 'https://cloud.mail.ru',
                },
                timeout: FETCH_TIMEOUT,
            });

            const data: SsoAuthResponseData = response.data;

            if (data.status !== 200) {
                throw new Error(`Auth token API returned status: ${data.status}`);
            }

            this.authToken = data.body.access_token;

            return this.authToken;
        } catch (error) {
            if (error instanceof Error) {
                console.error('Error in getAuthToken', error.message);
            }

            throw error;
        }
    }

    async fetchAccessToken() {
        try {
            const silentToken = await this.getSilentToken();
            return await this.fetchAuthToken(silentToken as string);
        } catch (error) {
            coreLoggerDispatchError('Fetch access token for superapp error', error);

            return null;
        }
    }
}
